"use client";

import { COLOR, IllustrationCard } from "@/components/learn/chapter-1/_shared";
import { useDocLocale } from "@/components/shared/docs-blocks";
import type { Locale } from "@/lib/i18n";

const STRINGS: Record<Locale, Record<string, string>> = {
  ja: {
    assetEquity: "株式",
    assetBond: "債券",
    assetCash: "現金等",
    amountEquity: "5,500万円",
    amountBond: "3,000万円",
    amountCash: "1,500万円",
    navHeading: "投資信託は、みんなのお金をまとめて運用する仕組み",
    navSub: "集まったお金の合計が「純資産総額」、それを1口分に割ったのが「基準価額」",
    fundAssets: "ファンドが持つ資産（純資産総額） = 1 億円",
    divideByUnits: "これを投資家全員の口数で割ると…",
    investors: "投資家",
    totalUnits: "計 10,000 口",
    navCalc: "1 億円 ÷ 10,000 口 =",
    navValue: "基準価額 10,000 円",
    perUnit: "（1口あたり）",
    navFooter: "ファンドの中身が値上がりすれば基準価額は上がり、分配などでお金が外に出れば下がる",
    navTitle: "基準価額とは — ファンドの「1口あたりの値段」",
    navDescription:
      "投資信託が持つ資産の合計（純資産総額）を、投資家全員の口数で割ったものが基準価額です。ファンドの中身が増えれば上がり、分配などでお金が外に出れば下がります。",
    fundNetAssets: "ファンドの純資産",
    redemptionLine: "繰上償還の目安ライン",
    hardToRun: "運営が難しくなりやすい水準",
    start100: "スタート 100億円",
    incomeBelowDist: "運用収益 < 分配金 の月が続く",
    belowGuide: "目安を下回る",
    distFooter: "分配が運用収益を上回る月が続くと、ファンドが小さくなり繰上償還されることもある",
    distTitle: "分配が続くと純資産は減り、運営が難しくなることもある",
    distDescription: "運用収益を上回る分配が続くと、ファンドの純資産は時間とともに減っていきます。",
    compoundHeading: "分配を続けると複利の差が広がる",
    compoundSub: "同じリターンでも、分配で外に出すと中で積み上がりにくい（20年シミュレーション）",
    gap: "差",
    noDistType: "無分配型（再投資）",
    monthlyDistType: "毎月分配型",
    compoundNote: "初期投資 100 / 年率リターン 5% を想定した概念図",
    compoundTitle: "分配で外に出すと、複利の積み上がりが弱くなる",
    compoundDescription:
      "同じリターンでも、毎月分配で資金が外に出ると、ファンド内で複利が効きにくくなります。20年で差は大きく開きます。",
    stage1Label: "毎月分配型",
    stage1Sub: "5万円 × 12か月",
    stage1Type: "年60万円が自動で外へ",
    stage1Reason: "必要額より多いこともある",
    stage2Label: "必要額を売却",
    stage2Sub: "3万円 × 12か月",
    stage2Type: "年36万円だけ外へ",
    stage2Reason: "取り崩し額を決めやすい",
    lifeHeading: "現金化するなら、外へ出す金額を見る",
    lifeSub: "必要額が月3万円なら、月5万円の分配は出しすぎになることがある",
    timePasses: "時間の経過",
    lifeNote: "年間で24万円分、余計にファンドから外へ出る例",
    lifeTitle: "毎月分配型と必要額売却では、外へ出る金額が違う",
    lifeDescription: "毎月5万円を受け取る設計と、必要な3万円だけ売却する設計を比べた単純例です。",
    trHeading: "分配金を受け取っていても、トータルでは損していることがある",
    trSub: "100万円を10年保有した場合の例（概念図）",
    nav: "基準価額",
    trStart: "100万円",
    atStart: "スタート時",
    trEndNav: "28万円",
    cumulativeDist: "累計分配金",
    trIncome: "+60万円",
    afterTenYears: "10年後",
    trTotal: "合計 88万円",
    trLoss: "万円",
    trFormula: "トータルリターン = いまの基準価額 ＋ 受け取った分配金",
    trTitle: "100万円を10年保有した場合のトータルリターン",
    trDescription:
      "分配金を累計60万円受け取っても、基準価額が28万円に下落していれば、全体では12万円のマイナスです。",
    caseA: "A. 収益 ＝ 分配",
    caseAsub: "収益の範囲で支払う",
    caseBsub: "不足を純資産から出す",
    caseB: "B. 収益 ＜ 分配",
    investmentIncome: "投資の運用収益",
    distributionToInvestors: "投資家への分配",
    effectOnNetAssets: "純資産への影響",
    roughlyMaintained: "おおむね維持",
    specialDistribution: "特別分配金（元本払戻）",
    shortfallCovered: "不足分を純資産から補填",
    scenTitle: "毎月の収益と分配のバランスで、ファンドの純資産は変わる",
    scenDescription:
      "収益の範囲で分配しているか、収益を超えて分配しているかで、純資産への影響はまったく違ってきます。",
    billionYen: "{v}億円",
    approxBillionYen: "約{v}億円",
    unitsSuffix: " 口",
    yearSuffix: "{v}年",
    tenThousandYen: "{v}万円",
  },
  en: {
    assetEquity: "Equities",
    assetBond: "Bonds",
    assetCash: "Cash & equivalents",
    amountEquity: "¥55.0m",
    amountBond: "¥30.0m",
    amountCash: "¥15.0m",
    navHeading: "A mutual fund pools everyone's money and runs it together",
    navSub: "The pooled total is “total net assets”; divided into one unit it becomes the “NAV”",
    fundAssets: "Assets the fund holds (total net assets) = ¥100m",
    divideByUnits: "Divide that by the units held by all investors…",
    investors: "Investors",
    totalUnits: "10,000 units in total",
    navCalc: "¥100m ÷ 10,000 units =",
    navValue: "NAV ¥10,000",
    perUnit: "(per unit)",
    navFooter: "If the holdings inside rise the NAV rises; if money leaves through distributions it falls",
    navTitle: "What NAV is — a fund's price per unit",
    navDescription:
      "NAV is the total of a fund's assets (total net assets) divided by the units held by all investors. It rises as the holdings grow and falls when money leaves through distributions.",
    fundNetAssets: "The fund's net assets",
    redemptionLine: "Rough threshold for early redemption",
    hardToRun: "A level where running the fund becomes difficult",
    start100: "Start: ¥10.0bn",
    incomeBelowDist: "Months where income < distribution continue",
    belowGuide: "Below the threshold",
    distFooter:
      "If months where distributions exceed income continue, the fund shrinks and can be redeemed early",
    distTitle: "Continued distributions shrink net assets and can make the fund hard to run",
    distDescription: "When distributions exceeding income continue, a fund's net assets fall over time.",
    compoundHeading: "Keep distributing and the compounding gap widens",
    compoundSub:
      "For the same return, money sent out as distributions builds up less inside (a 20-year simulation)",
    gap: "Gap",
    noDistType: "Non-distributing (reinvesting)",
    monthlyDistType: "Monthly distributing",
    compoundNote: "A conceptual chart assuming an initial 100 and a 5% annual return",
    compoundTitle: "Sending money out as distributions weakens compounding",
    compoundDescription:
      "For the same return, money leaving monthly means compounding works less inside the fund. Over 20 years the gap widens considerably.",
    stage1Label: "Monthly distributing",
    stage1Sub: "¥50,000 × 12 months",
    stage1Type: "¥600,000 a year leaves automatically",
    stage1Reason: "Sometimes more than you need",
    stage2Label: "Selling what you need",
    stage2Sub: "¥30,000 × 12 months",
    stage2Type: "Only ¥360,000 a year leaves",
    stage2Reason: "Easier to set the drawdown amount",
    lifeHeading: "If you need cash, look at the amount leaving the fund",
    lifeSub: "If you need ¥30,000 a month, a ¥50,000 distribution can be more than necessary",
    timePasses: "Time passes",
    lifeNote: "An example where ¥240,000 a year leaves the fund unnecessarily",
    lifeTitle: "Monthly distributing and selling what you need send out different amounts",
    lifeDescription:
      "A simple comparison between receiving ¥50,000 a month and selling only the ¥30,000 you need.",
    trHeading: "You can be receiving distributions and still be down overall",
    trSub: "An example of holding ¥1,000,000 for ten years (conceptual)",
    nav: "NAV",
    trStart: "¥1,000,000",
    atStart: "At the start",
    trEndNav: "¥280,000",
    cumulativeDist: "Cumulative distributions",
    trIncome: "+¥600,000",
    afterTenYears: "After 10 years",
    trTotal: "Total ¥880,000",
    trLoss: "",
    trFormula: "Total return = current NAV + distributions received",
    trTitle: "Total return on holding ¥1,000,000 for ten years",
    trDescription:
      "Even having received ¥600,000 in cumulative distributions, if the NAV has fallen to ¥280,000 you are ¥120,000 down overall.",
    caseA: "A. Income = distribution",
    caseAsub: "Paid within income",
    caseBsub: "The shortfall comes out of net assets",
    caseB: "B. Income < distribution",
    investmentIncome: "Investment income",
    distributionToInvestors: "Distribution to investors",
    effectOnNetAssets: "Effect on net assets",
    roughlyMaintained: "Roughly maintained",
    specialDistribution: "Special distribution (return of principal)",
    shortfallCovered: "The shortfall is covered from net assets",
    scenTitle: "A fund's net assets change with the monthly balance of income and distribution",
    scenDescription:
      "Whether it distributes within income or beyond it makes an entirely different difference to net assets.",
    billionYen: "¥{v}00m",
    approxBillionYen: "approx. ¥{v}00m",
    unitsSuffix: " units",
    yearSuffix: "Yr {v}",
    tenThousandYen: "¥{v}0k",
  },
};

// ── Colors ────────────────────────────────────────────────────────

const C = {
  nav: COLOR.sky,
  dist: COLOR.rose,
  accum: COLOR.emerald,
  warn: COLOR.amber,
  accent: "#7c3aed",
};

// ━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━
// Diagram 0: What is NAV (基準価額) and Net Assets (純資産総額)
// ━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━

// ── Person icon (simple head + shoulders) ──
function PersonIcon({ x, y, color, size = 1 }: { x: number; y: number; color: string; size?: number }) {
  return (
    <g transform={`translate(${x}, ${y}) scale(${size})`} opacity={0.7}>
      <circle cx={0} cy={-5} r={4} fill={color} fillOpacity={0.25} stroke={color} strokeWidth="0.8" />
      <path d="M -6 6 Q -6 0 0 -1 Q 6 0 6 6" fill={color} fillOpacity={0.2} stroke={color} strokeWidth="0.8" />
    </g>
  );
}

function NAVExplainerIllustration() {
  const t = STRINGS[useDocLocale()];
  const VW = 460;
  const VH = 280;

  // ── Row 1: The fund pool ──
  const poolCX = VW / 2;
  const poolY = 46;
  const poolW = 360;
  const poolH = 70;

  const assets = [
    { key: "Equity", pct: 55, color: C.nav },
    { key: "Bond", pct: 30, color: C.accum },
    { key: "Cash", pct: 15, color: C.warn },
  ];

  // ── Row 2: Division ──
  const divY = 168;

  // Person positions for "investors" — each with their own unit holdings
  const investorUnits = [1500, 2500, 2000, 3000, 1000]; // 10,000 units in total
  const personCount = investorUnits.length;
  const personSpacing = 56;
  const personsStartX = poolCX - ((personCount - 1) * personSpacing) / 2;

  return (
    <svg viewBox={`0 0 ${VW} ${VH}`} className="mx-auto w-full max-w-xl">
      {/* ── Row 1: What is the fund's 純資産総額 ── */}
      <text x={poolCX} y={18} textAnchor="middle" fontSize="11" fill="currentColor" fontWeight="700">
        {t.navHeading}
      </text>
      <text x={poolCX} y={31} textAnchor="middle" fontSize="8.5" fill="currentColor" fillOpacity={0.55}>
        {t.navSub}
      </text>

      {/* Pool container */}
      <rect x={poolCX - poolW / 2} y={poolY} width={poolW} height={poolH} rx={10} fillOpacity={0.05} />

      {/* Stacked asset bars inside pool */}
      {(() => {
        const barY = poolY + 26;
        const barH = 28;
        const barTotalW = poolW - 30;
        const barStartX = poolCX - barTotalW / 2;
        let offset = 0;
        return assets.map((a) => {
          const w = (a.pct / 100) * barTotalW;
          const bx = barStartX + offset;
          offset += w;
          return (
            <g key={a.key}>
              <rect
                x={bx}
                y={barY}
                width={w}
                height={barH}
                rx={4}
                fill={a.color}
                fillOpacity={0.15}
                stroke={a.color}
                strokeWidth="0.8"
                strokeOpacity={0.4}
              />
              <text x={bx + w / 2} y={barY + 12} textAnchor="middle" fontSize="8" fill={a.color} fontWeight="600">
                {t[`asset${a.key}`]}
              </text>
              <text
                x={bx + w / 2}
                y={barY + 23}
                textAnchor="middle"
                fontSize="7.5"
                fill={a.color}
                fontWeight="500"
                fillOpacity={0.7}
              >
                {t[`amount${a.key}`]}
              </text>
            </g>
          );
        });
      })()}

      {/* Pool label */}
      <text x={poolCX} y={poolY + 12} textAnchor="middle" fontSize="9" fill={C.nav} fontWeight="700">
        {t.fundAssets}
      </text>

      {/* ── Connecting arrow ── */}
      <defs>
        <marker id="navArrow" markerWidth="6" markerHeight="6" refX="5" refY="3" orient="auto">
          <path d="M 0 0 L 6 3 L 0 6" fill="none" stroke="currentColor" strokeWidth="1" strokeOpacity={0.3} />
        </marker>
      </defs>
      <line
        x1={poolCX}
        y1={poolY + poolH + 6}
        x2={poolCX}
        y2={poolY + poolH + 28}
        stroke="currentColor"
        strokeWidth="1"
        strokeOpacity={0.15}
        markerEnd="url(#navArrow)"
      />
      <text x={poolCX} y={poolY + poolH + 22} textAnchor="middle" fontSize="8" fill="currentColor" fillOpacity={0.35}>
        {t.divideByUnits}
      </text>

      {/* ── Row 2: Division into per-unit ── */}

      {/* Investors row with individual unit holdings */}
      <text x={personsStartX - 18} y={divY + 2} textAnchor="end" fontSize="8.5" fill={C.accent} fontWeight="600">
        {t.investors}
      </text>
      {investorUnits.map((units, i) => {
        const px = personsStartX + i * personSpacing;
        return (
          <g key={i}>
            <PersonIcon x={px} y={divY} color={C.accent} />
            <text x={px} y={divY + 14} textAnchor="middle" fontSize="7.5" fill={C.accent} fontWeight="600">
              {units.toLocaleString()}
              {t.unitsSuffix}
            </text>
          </g>
        );
      })}
      <text
        x={personsStartX + (personCount - 1) * personSpacing + 20}
        y={divY + 2}
        fontSize="8"
        fill={C.accent}
        fillOpacity={0.55}
      >
        {t.totalUnits}
      </text>

      {/* Arrow down to result */}
      <line
        x1={poolCX}
        y1={divY + 22}
        x2={poolCX}
        y2={divY + 40}
        stroke="currentColor"
        strokeWidth="1"
        strokeOpacity={0.15}
        markerEnd="url(#navArrow)"
      />

      {/* NAV result — big and clear */}
      {(() => {
        const resultY = divY + 44;
        const resultW = 280;
        const resultH = 48;
        return (
          <g>
            <rect
              x={poolCX - resultW / 2}
              y={resultY}
              width={resultW}
              height={resultH}
              rx={10}
              fill={C.nav}
              fillOpacity={0.08}
              stroke={C.nav}
              strokeWidth="1.5"
              strokeOpacity={0.45}
            />
            {/* Formula */}
            <text
              x={poolCX}
              y={resultY + 18}
              textAnchor="middle"
              fontSize="8.5"
              fill={C.nav}
              fontWeight="500"
              fillOpacity={0.7}
            >
              {t.navCalc}
            </text>
            <text x={poolCX} y={resultY + 36} textAnchor="middle" fontSize="15" fill={C.nav} fontWeight="800">
              {t.navValue}
            </text>
            {/* Sub label */}
            <text
              x={poolCX + resultW / 2 + 6}
              y={resultY + 30}
              fontSize="7.5"
              fill={C.nav}
              fillOpacity={0.5}
              textAnchor="start"
            >
              {t.perUnit}
            </text>
          </g>
        );
      })()}

      {/* ── Bottom note ── */}
      <text x={poolCX} y={VH - 10} textAnchor="middle" fontSize="8.5" fill="currentColor" fillOpacity={0.45}>
        {t.navFooter}
      </text>
    </svg>
  );
}

export function DocNAVExplainerDiagram() {
  const t = STRINGS[useDocLocale()];
  return (
    <IllustrationCard
      title={t.navTitle}
      description={t.navDescription}
    >
      <NAVExplainerIllustration />
    </IllustrationCard>
  );
}

// ━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━
// Diagram 1: Distribution reduces NAV
// ━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━

function DistributionMechanismIllustration() {
  const t = STRINGS[useDocLocale()];
  const VW = 460;
  const VH = 260; // 260 -> 236
  const chartL = 52;
  const chartR = VW - 18;
  const chartT = 34; // 58 -> 34
  const chartB = 196; // 208 -> 196
  const chartW = chartR - chartL;
  const chartH = chartB - chartT;

  // 10年（120か月）の純資産の推移を概念的にシミュレート。
  // 運用収益より分配金が多い月が続き、途中で相場ショックもある前提。
  const months = 120;
  const pts = months + 1;
  const navValues: number[] = [];
  for (let i = 0; i < pts; i++) {
    const trend = 100 * Math.pow(0.988, i);
    const wave = 3.8 * Math.sin(i * 0.28) + 1.8 * Math.sin(i * 0.7 + 1);
    const shock = i >= 22 && i <= 34 ? -8 * Math.sin(((i - 22) / 12) * Math.PI) : 0;
    navValues.push(Math.max(trend + wave + shock, 2));
  }

  const yMax = 120;
  const yMin = 0;
  const toX = (m: number) => chartL + (m / months) * chartW;
  const toY = (v: number) => chartB - ((v - yMin) / (yMax - yMin)) * chartH;

  const linePath = navValues.map((v, i) => `${i === 0 ? "M" : "L"} ${toX(i)} ${toY(v)}`).join(" ");
  const areaPath = `${linePath} L ${toX(months)} ${chartB} L ${toX(0)} ${chartB} Z`;

  const threshold = 30;
  const thresholdY = toY(threshold);
  const crossIdx = navValues.findIndex((v) => v < threshold);

  const yTicks = [0, 30, 60, 100];
  const xTicks = [0, 24, 48, 72, 96, 120];

  const startVal = navValues[0];
  const endVal = navValues[months];

  return (
    <svg viewBox={`0 0 ${VW} ${VH}`} className="mx-auto block w-full max-w-xl">
      {/* Legend */}
      <line x1={chartL} y1={22} x2={chartL + 14} y2={22} stroke={C.dist} strokeWidth="1.8" />
      <text x={chartL + 18} y={25} fontSize="7.5" fill={C.dist} fontWeight="600">
        {t.fundNetAssets}
      </text>
      <line
        x1={chartL + 110}
        y1={22}
        x2={chartL + 124}
        y2={22}
        stroke={C.warn}
        strokeWidth="1.2"
        strokeDasharray="4 3"
      />
      <text x={chartL + 128} y={25} fontSize="7.5" fill={C.warn} fontWeight="600">
        {t.redemptionLine}
      </text>
      {/* Y axis grid */}
      {yTicks.map((v) => (
        <g key={v}>
          <line
            x1={chartL}
            y1={toY(v)}
            x2={chartR}
            y2={toY(v)}
            stroke="currentColor"
            strokeOpacity={0.08}
            strokeDasharray="3 6"
          />
          <text x={chartL - 6} y={toY(v) + 3} textAnchor="end" fontSize="7" fill="currentColor" fillOpacity={0.45}>
            {t.billionYen.replace("{v}", String(v))}
          </text>
        </g>
      ))}
      {/* X axis labels */}
      {xTicks.map((m) => (
        <text
          key={m}
          x={toX(m)}
          y={chartB + 13}
          textAnchor="middle"
          fontSize="7"
          fill="currentColor"
          fillOpacity={0.45}
        >
          {t.yearSuffix.replace("{v}", String(m / 12))}
        </text>
      ))}
      {/* Danger band below threshold */}
      <rect x={chartL} y={thresholdY} width={chartW} height={chartB - thresholdY} fill={C.warn} fillOpacity={0.06} />
      <line
        x1={chartL}
        y1={thresholdY}
        x2={chartR}
        y2={thresholdY}
        stroke={C.warn}
        strokeWidth="1"
        strokeDasharray="4 3"
        strokeOpacity={0.65}
      />
      <text x={chartR - 4} y={thresholdY + 11} textAnchor="end" fontSize="7" fill={C.warn} fillOpacity={0.7}>
        {t.hardToRun}
      </text>
      {/* Area + line */}
      <path d={areaPath} fill={C.dist} fillOpacity={0.12} />
      <path d={linePath} fill="none" stroke={C.dist} strokeWidth="1.8" />
      {/* Start marker */}
      <circle cx={toX(0)} cy={toY(startVal)} r={3} fill="white" stroke={C.dist} strokeWidth="1.5" />
      <text x={toX(0) + 6} y={toY(startVal) - 6} fontSize="8" fill={C.dist} fontWeight="700">
        {t.start100}
      </text>
      {/* Mid annotation — distribution > return phase */}
      {(() => {
        const m = 54;
        const ax = toX(m);
        const ay = toY(navValues[m]);
        return (
          <g>
            <line
              x1={ax}
              y1={ay - 6}
              x2={ax}
              y2={ay - 24}
              stroke="currentColor"
              strokeOpacity={0.25}
              strokeWidth="0.8"
            />
            <text
              x={ax}
              y={ay - 28}
              textAnchor="middle"
              fontSize="7.5"
              fill="currentColor"
              fillOpacity={0.6}
              fontWeight="600"
            >
              {t.incomeBelowDist}
            </text>
          </g>
        );
      })()}
      {/* Crossing point — below threshold */}
      {crossIdx > 0 && (
        <g>
          <circle
            cx={toX(crossIdx)}
            cy={toY(navValues[crossIdx])}
            r={3.2}
            fill={C.warn}
            stroke="white"
            strokeWidth="1"
          />
          <line
            x1={toX(crossIdx)}
            y1={toY(navValues[crossIdx]) - 4}
            x2={toX(crossIdx)}
            y2={toY(navValues[crossIdx]) - 22}
            stroke={C.warn}
            strokeWidth="0.8"
            strokeOpacity={0.55}
            strokeDasharray="2 2"
          />
          <text
            x={toX(crossIdx)}
            y={toY(navValues[crossIdx]) - 26}
            textAnchor="middle"
            fontSize="7.5"
            fill={C.warn}
            fontWeight="700"
          >
            {t.belowGuide}
          </text>
        </g>
      )}
      {/* End marker */}
      <circle cx={toX(months)} cy={toY(endVal)} r={3.2} fill="white" stroke={C.dist} strokeWidth="1.5" />
      <text x={toX(months) - 4} y={toY(endVal) + 14} textAnchor="end" fontSize="8" fill={C.dist} fontWeight="700">
        {t.approxBillionYen.replace("{v}", String(Math.round(endVal)))}
      </text>
      {/* Bottom note */}
      <rect
        x={46}
        y={228}
        width={368}
        height={26}
        rx={8}
        fill={C.dist}
        fillOpacity={0.08}
        stroke={C.dist}
        strokeOpacity={0.24}
      />
      <text x={VW / 2} y={244} textAnchor="middle" fontSize="9" fill={C.dist} fontWeight="700">
        {t.distFooter}
      </text>
    </svg>
  );
}

export function DocDistributionMechanismDiagram() {
  const t = STRINGS[useDocLocale()];
  return (
    <IllustrationCard
      title={t.distTitle}
      description={t.distDescription}
    >
      <DistributionMechanismIllustration />
    </IllustrationCard>
  );
}

// ━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━
// Diagram 2: Compound growth — distribution vs accumulation
// ━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━

function CompoundComparisonIllustration() {
  const t = STRINGS[useDocLocale()];
  const VW = 460;
  const VH = 220;
  const chartL = 55;
  const chartR = VW - 25;
  const chartT = 55;
  const chartB = 175;
  const chartW = chartR - chartL;
  const chartH = chartB - chartT;

  // Simulate 20 years, 5% annual return, monthly distribution drains ~3%
  const years = 20;
  const pts = years + 1;
  const accumRate = 1.05;
  const distRate = 1.02; // net after distribution drag

  const accumValues: number[] = [];
  const distValues: number[] = [];
  for (let i = 0; i < pts; i++) {
    accumValues.push(100 * Math.pow(accumRate, i));
    distValues.push(100 * Math.pow(distRate, i));
  }

  const maxVal = accumValues[accumValues.length - 1];
  const minVal = 80;

  function toX(i: number) {
    return chartL + (i / (pts - 1)) * chartW;
  }
  function toY(v: number) {
    return chartB - ((v - minVal) / (maxVal - minVal)) * chartH;
  }

  const accumPath = accumValues.map((v, i) => `${i === 0 ? "M" : "L"} ${toX(i)} ${toY(v)}`).join(" ");
  const distPath = distValues.map((v, i) => `${i === 0 ? "M" : "L"} ${toX(i)} ${toY(v)}`).join(" ");

  // Area fills
  const accumArea = `${accumPath} L ${toX(pts - 1)} ${chartB} L ${toX(0)} ${chartB} Z`;
  const distArea = `${distPath} L ${toX(pts - 1)} ${chartB} L ${toX(0)} ${chartB} Z`;

  // Grid lines
  const gridValues = [100, 150, 200, 250];

  return (
    <svg viewBox={`0 0 ${VW} ${VH}`} className="mx-auto w-full max-w-xl">
      {/* Title */}
      <text x={VW / 2} y={18} textAnchor="middle" fontSize="11" fill="currentColor" fontWeight="700">
        {t.compoundHeading}
      </text>
      <text x={VW / 2} y={31} textAnchor="middle" fontSize="8.5" fill="currentColor" fillOpacity={0.55}>
        {t.compoundSub}
      </text>

      {/* Y axis */}
      {gridValues.map((v) => (
        <g key={v}>
          <line
            x1={chartL}
            y1={toY(v)}
            x2={chartR}
            y2={toY(v)}
            stroke="currentColor"
            strokeOpacity={0.08}
            strokeDasharray="3 6"
          />
          <text x={chartL - 5} y={toY(v) + 3} textAnchor="end" fontSize="7" fill="currentColor" fillOpacity={0.4}>
            {v}
          </text>
        </g>
      ))}

      {/* X axis labels */}
      {[0, 5, 10, 15, 20].map((yr) => (
        <text
          key={yr}
          x={toX(yr)}
          y={chartB + 14}
          textAnchor="middle"
          fontSize="7"
          fill="currentColor"
          fillOpacity={0.4}
        >
          {t.yearSuffix.replace("{v}", String(yr))}
        </text>
      ))}

      {/* Accumulation area + line */}
      <path d={accumArea} fill={C.accum} fillOpacity={0.08} />
      <path d={accumPath} fill="none" stroke={C.accum} strokeWidth="2" />

      {/* Distribution area + line */}
      <path d={distArea} fill={C.dist} fillOpacity={0.06} />
      <path d={distPath} fill="none" stroke={C.dist} strokeWidth="2" strokeDasharray="6 3" />

      {/* End dots */}
      <circle
        cx={toX(pts - 1)}
        cy={toY(accumValues[pts - 1])}
        r={3.5}
        fill="white"
        stroke={C.accum}
        strokeWidth="1.5"
      />
      <circle cx={toX(pts - 1)} cy={toY(distValues[pts - 1])} r={3.5} fill="white" stroke={C.dist} strokeWidth="1.5" />

      {/* End labels */}
      <text
        x={toX(pts - 1) + 2}
        y={toY(accumValues[pts - 1]) - 8}
        textAnchor="end"
        fontSize="9"
        fill={C.accum}
        fontWeight="700"
      >
        {Math.round(accumValues[pts - 1])}
      </text>
      <text
        x={toX(pts - 1) + 2}
        y={toY(distValues[pts - 1]) - 8}
        textAnchor="end"
        fontSize="9"
        fill={C.dist}
        fontWeight="700"
      >
        {Math.round(distValues[pts - 1])}
      </text>

      {/* Gap arrow */}
      {(() => {
        const gx = toX(pts - 1) + 14;
        const gy1 = toY(accumValues[pts - 1]);
        const gy2 = toY(distValues[pts - 1]);
        return (
          <g>
            <line x1={gx} y1={gy1 + 4} x2={gx} y2={gy2 - 4} stroke={C.warn} strokeWidth="1.2" />
            <line x1={gx - 3} y1={gy1 + 4} x2={gx + 3} y2={gy1 + 4} stroke={C.warn} strokeWidth="1.2" />
            <line x1={gx - 3} y1={gy2 - 4} x2={gx + 3} y2={gy2 - 4} stroke={C.warn} strokeWidth="1.2" />
            <text x={gx + 6} y={(gy1 + gy2) / 2 + 3} fontSize="7.5" fill={C.warn} fontWeight="600">
              {t.gap}
            </text>
          </g>
        );
      })()}

      {/* Legend */}
      <line x1={chartL + 10} y1={chartT - 8} x2={chartL + 26} y2={chartT - 8} stroke={C.accum} strokeWidth="2" />
      <text x={chartL + 30} y={chartT - 5} fontSize="7.5" fill={C.accum} fontWeight="600">
        {t.noDistType}
      </text>
      <line
        x1={chartL + 120}
        y1={chartT - 8}
        x2={chartL + 136}
        y2={chartT - 8}
        stroke={C.dist}
        strokeWidth="2"
        strokeDasharray="6 3"
      />
      <text x={chartL + 140} y={chartT - 5} fontSize="7.5" fill={C.dist} fontWeight="600">
        {t.monthlyDistType}
      </text>

      {/* Bottom note */}
      <text x={VW / 2} y={VH - 6} textAnchor="middle" fontSize="8" fill="currentColor" fillOpacity={0.4}>
        {t.compoundNote}
      </text>
    </svg>
  );
}

export function DocCompoundComparisonDiagram() {
  const t = STRINGS[useDocLocale()];
  return (
    <IllustrationCard
      title={t.compoundTitle}
      description={t.compoundDescription}
    >
      <CompoundComparisonIllustration />
    </IllustrationCard>
  );
}

// ━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━
// Diagram 3: Life stage suitability
// ━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━

type StageInfo = {
  stagePrefix: "stage1" | "stage2";
  color: string;
};

const STAGES: StageInfo[] = [
  {
    stagePrefix: "stage1",
    color: C.accum,

  },
  {
    stagePrefix: "stage2",
    color: C.dist,

  },
];

function LifeStageSuitabilityIllustration() {
  const t = STRINGS[useDocLocale()];
  const VW = 460;
  const VH = 165;
  const boxW = 185;
  const boxH = 82;
  const gap = 30;
  const totalW = STAGES.length * boxW + gap;
  const startX = (VW - totalW) / 2;
  const boxY = 50;

  return (
    <svg viewBox={`0 0 ${VW} ${VH}`} className="mx-auto w-full max-w-xl">
      {/* Title */}
      <text x={VW / 2} y={18} textAnchor="middle" fontSize="11" fill="currentColor" fontWeight="700">
        {t.lifeHeading}
      </text>
      <text x={VW / 2} y={31} textAnchor="middle" fontSize="8.5" fill="currentColor" fillOpacity={0.55}>
        {t.lifeSub}
      </text>

      {STAGES.map((stage, i) => {
        const bx = startX + i * (boxW + gap);
        const cx = bx + boxW / 2;

        return (
          <g key={stage.stagePrefix}>
            {/* Box */}
            <rect
              x={bx}
              y={boxY}
              width={boxW}
              height={boxH}
              rx={8}
              fill={stage.color}
              fillOpacity={0.07}
              stroke={stage.color}
              strokeWidth="1"
              strokeOpacity={0.35}
            />

            {/* Stage label */}
            <text x={cx} y={boxY + 18} textAnchor="middle" fontSize="10" fill={stage.color} fontWeight="700">
              {t[`${stage.stagePrefix}Label`]}
            </text>
            <text x={cx} y={boxY + 30} textAnchor="middle" fontSize="7" fill={stage.color} fillOpacity={0.55}>
              {t[`${stage.stagePrefix}Sub`]}
            </text>

            {/* Divider */}
            <line
              x1={bx + 12}
              y1={boxY + 38}
              x2={bx + boxW - 12}
              y2={boxY + 38}
              stroke={stage.color}
              strokeOpacity={0.15}
              strokeWidth="0.5"
            />

            {/* Type recommendation */}
            <text x={cx} y={boxY + 54} textAnchor="middle" fontSize="9" fill={stage.color} fontWeight="600">
              {t[`${stage.stagePrefix}Type`]}
            </text>
            <text x={cx} y={boxY + 68} textAnchor="middle" fontSize="7.5" fill={stage.color} fillOpacity={0.6}>
              {t[`${stage.stagePrefix}Reason`]}
            </text>
          </g>
        );
      })}

      {/* Arrow between stages */}
      <line
        x1={startX + boxW + 6}
        y1={boxY + boxH / 2}
        x2={startX + boxW + gap - 6}
        y2={boxY + boxH / 2}
        stroke="currentColor"
        strokeWidth="1"
        strokeOpacity={0.15}
      />
      <text
        x={startX + boxW + gap / 2}
        y={boxY + boxH / 2 - 6}
        textAnchor="middle"
        fontSize="7"
        fill="currentColor"
        fillOpacity={0.35}
      >
        {t.timePasses}
      </text>

      {/* Bottom note */}
      <text x={VW / 2} y={VH - 8} textAnchor="middle" fontSize="8" fill="currentColor" fillOpacity={0.4}>
        {t.lifeNote}
      </text>
    </svg>
  );
}

export function DocLifeStageSuitabilityDiagram() {
  const t = STRINGS[useDocLocale()];
  return (
    <IllustrationCard
      title={t.lifeTitle}
      description={t.lifeDescription}
    >
      <LifeStageSuitabilityIllustration />
    </IllustrationCard>
  );
}

const TR_START = 100;
const TR_END_NAV = 28;
const TR_END_INCOME = 60;
const TR_END_TOTAL = TR_END_NAV + TR_END_INCOME;
const TR_LOSS = TR_START - TR_END_TOTAL;

function InvestorReturnBreakdownIllustration() {
  const t = STRINGS[useDocLocale()];
  const VW = 460;
  const VH = 268;
  const chartT = 62;
  const chartB = 208;
  const chartH = chartB - chartT;
  const yMax = 120;
  const toY = (v: number) => chartB - (v / yMax) * chartH;

  const barW = 100;
  const leftX = 110;
  const rightX = 252;

  return (
    <svg viewBox={`0 0 ${VW} ${VH}`} className="mx-auto w-full max-w-xl">
      <text x={VW / 2} y={20} textAnchor="middle" fontSize="11.5" fill="currentColor" fontWeight="700">
        {t.trHeading}
      </text>
      <text x={VW / 2} y={34} textAnchor="middle" fontSize="8.5" fill="currentColor" fillOpacity={0.55}>
        {t.trSub}
      </text>

      {[0, 50, 100].map((v) => (
        <g key={v}>
          <line
            x1={60}
            y1={toY(v)}
            x2={VW - 20}
            y2={toY(v)}
            stroke="currentColor"
            strokeOpacity={0.08}
            strokeDasharray="3 6"
          />
          <text x={55} y={toY(v) + 3} textAnchor="end" fontSize="7.5" fill="currentColor" fillOpacity={0.45}>
            {t.tenThousandYen.replace("{v}", String(v))}
          </text>
        </g>
      ))}

      <line
        x1={leftX + barW}
        y1={toY(TR_START)}
        x2={rightX + barW + 30}
        y2={toY(TR_START)}
        stroke={C.nav}
        strokeWidth="0.8"
        strokeDasharray="3 3"
        strokeOpacity={0.55}
      />

      <rect
        x={leftX}
        y={toY(TR_START)}
        width={barW}
        height={chartB - toY(TR_START)}
        rx={4}
        fill={C.nav}
        fillOpacity={0.22}
        stroke={C.nav}
        strokeWidth="1"
        strokeOpacity={0.5}
      />
      <text
        x={leftX + barW / 2}
        y={(toY(TR_START) + chartB) / 2 - 2}
        textAnchor="middle"
        fontSize="9"
        fill={C.nav}
        fontWeight="700"
      >
        {t.nav}
      </text>
      <text
        x={leftX + barW / 2}
        y={(toY(TR_START) + chartB) / 2 + 12}
        textAnchor="middle"
        fontSize="10"
        fill={C.nav}
        fontWeight="700"
      >
        {t.trStart}
      </text>
      <text x={leftX + barW / 2} y={chartB + 15} textAnchor="middle" fontSize="9" fill="currentColor" fontWeight="700">
        {t.atStart}
      </text>

      <rect
        x={rightX}
        y={toY(TR_END_NAV)}
        width={barW}
        height={chartB - toY(TR_END_NAV)}
        rx={4}
        fill={C.nav}
        fillOpacity={0.22}
        stroke={C.nav}
        strokeWidth="1"
        strokeOpacity={0.5}
      />
      <text
        x={rightX + barW / 2}
        y={(toY(TR_END_NAV) + chartB) / 2 - 2}
        textAnchor="middle"
        fontSize="9"
        fill={C.nav}
        fontWeight="700"
      >
        {t.nav}
      </text>
      <text
        x={rightX + barW / 2}
        y={(toY(TR_END_NAV) + chartB) / 2 + 12}
        textAnchor="middle"
        fontSize="10"
        fill={C.nav}
        fontWeight="700"
      >
        {t.trEndNav}
      </text>

      <rect
        x={rightX}
        y={toY(TR_END_TOTAL)}
        width={barW}
        height={toY(TR_END_NAV) - toY(TR_END_TOTAL)}
        rx={4}
        fill={C.warn}
        fillOpacity={0.3}
        stroke={C.warn}
        strokeWidth="1"
        strokeOpacity={0.55}
      />
      <text
        x={rightX + barW / 2}
        y={(toY(TR_END_TOTAL) + toY(TR_END_NAV)) / 2 - 1}
        textAnchor="middle"
        fontSize="9"
        fill={C.warn}
        fontWeight="700"
      >
        {t.cumulativeDist}
      </text>
      <text
        x={rightX + barW / 2}
        y={(toY(TR_END_TOTAL) + toY(TR_END_NAV)) / 2 + 12}
        textAnchor="middle"
        fontSize="10"
        fill={C.warn}
        fontWeight="700"
      >
        {t.trIncome}
      </text>

      <text x={rightX + barW / 2} y={chartB + 15} textAnchor="middle" fontSize="9" fill="currentColor" fontWeight="700">
        {t.afterTenYears}
      </text>

      <text
        x={rightX + barW / 2}
        y={toY(TR_END_TOTAL) - 8}
        textAnchor="middle"
        fontSize="11"
        fill={C.dist}
        fontWeight="800"
      >
        {t.trTotal}
      </text>
      <text
        x={rightX + barW / 2}
        y={toY(TR_END_TOTAL) - 22}
        textAnchor="middle"
        fontSize="8.5"
        fill={C.dist}
        fontWeight="700"
      >
        （−12%）
      </text>

      {(() => {
        const bx = rightX + barW + 10;
        const y1 = toY(TR_START);
        const y2 = toY(TR_END_TOTAL);
        return (
          <g>
            <line x1={bx} y1={y1} x2={bx} y2={y2} stroke={C.dist} strokeWidth="1.2" />
            <line x1={bx - 3} y1={y1} x2={bx + 3} y2={y1} stroke={C.dist} strokeWidth="1.2" />
            <line x1={bx - 3} y1={y2} x2={bx + 3} y2={y2} stroke={C.dist} strokeWidth="1.2" />
            <text x={bx + 6} y={(y1 + y2) / 2 + 3} fontSize="9" fill={C.dist} fontWeight="700">
              −{TR_LOSS}{t.trLoss}
            </text>
          </g>
        );
      })()}

      <text
        x={VW / 2}
        y={VH - 8}
        textAnchor="middle"
        fontSize="9"
        fill="currentColor"
        fillOpacity={0.6}
        fontWeight="600"
      >
        {t.trFormula}
      </text>
    </svg>
  );
}

export function DocInvestorReturnBreakdownDiagram() {
  const t = STRINGS[useDocLocale()];
  return (
    <IllustrationCard
      title={t.trTitle}
      description={t.trDescription}
    >
      <InvestorReturnBreakdownIllustration />
    </IllustrationCard>
  );
}

// ━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━
// Diagram 5: 3 monthly balance scenarios (income vs distribution)
// ━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━

function MonthlyBalanceScenariosIllustration() {
  const t = STRINGS[useDocLocale()];
  const VW = 460;
  const VH = 212;

  const panelW = 198;
  const panelH = 194;
  const gap = 24;
  const startX = (VW - panelW * 2 - gap) / 2;
  const panelY = 10;

  // 月次の最大値スケール（単位：億円）
  const maxMonthly = 5;
  const barAreaX = 14;
  const barAreaW = panelW - 28;
  const barH = 14;

  const fmt = (v: number) => t.billionYen.replace("{v}", String(v));

  function scenarioA(px: number) {
    const income = 5;
    const dist = 5;
    const incomeW = (income / maxMonthly) * barAreaW;
    const distW = (dist / maxMonthly) * barAreaW;

    return (
      <g key="A">
        <rect
          x={px}
          y={panelY}
          width={panelW}
          height={panelH}
          rx={8}
          fill={C.accum}
          fillOpacity={0.05}
          stroke={C.accum}
          strokeOpacity={0.35}
        />
        <text x={px + panelW / 2} y={panelY + 18} textAnchor="middle" fontSize="11" fill={C.accum} fontWeight="800">
          {t.caseA}
        </text>
        <text
          x={px + panelW / 2}
          y={panelY + 31}
          textAnchor="middle"
          fontSize="8"
          fill="currentColor"
          fillOpacity={0.55}
        >
          {t.caseAsub}
        </text>

        <text x={px + barAreaX} y={panelY + 52} fontSize="8" fill="currentColor" fillOpacity={0.6} fontWeight="600">
          {t.investmentIncome}
        </text>
        <rect
          x={px + barAreaX}
          y={panelY + 58}
          width={incomeW}
          height={barH}
          rx={2}
          fill={C.nav}
          fillOpacity={0.3}
          stroke={C.nav}
          strokeOpacity={0.6}
        />
        <text
          x={px + barAreaX + incomeW - 4}
          y={panelY + 68}
          textAnchor="end"
          fontSize="9"
          fill={C.nav}
          fontWeight="700"
        >
          +{fmt(income)}
        </text>

        <text x={px + barAreaX} y={panelY + 90} fontSize="8" fill="currentColor" fillOpacity={0.6} fontWeight="600">
          {t.distributionToInvestors}
        </text>
        <rect
          x={px + barAreaX}
          y={panelY + 96}
          width={distW}
          height={barH}
          rx={2}
          fill={C.dist}
          fillOpacity={0.32}
          stroke={C.dist}
          strokeOpacity={0.55}
        />
        <text
          x={px + barAreaX + distW - 4}
          y={panelY + 106}
          textAnchor="end"
          fontSize="9"
          fill={C.dist}
          fontWeight="700"
        >
          −{fmt(dist)}
        </text>

        <rect
          x={px + 12}
          y={panelY + 128}
          width={panelW - 24}
          height={54}
          rx={6}
          fill={C.accum}
          fillOpacity={0.1}
          stroke={C.accum}
          strokeOpacity={0.5}
        />
        <text x={px + panelW / 2} y={panelY + 144} textAnchor="middle" fontSize="8.5" fill={C.accum} fontWeight="700">
          {t.effectOnNetAssets}
        </text>
        <text x={px + panelW / 2} y={panelY + 163} textAnchor="middle" fontSize="15" fill={C.accum} fontWeight="800">
          ±0
        </text>
        <text x={px + panelW / 2} y={panelY + 176} textAnchor="middle" fontSize="8" fill={C.accum} fillOpacity={0.8}>
          {t.roughlyMaintained}
        </text>
      </g>
    );
  }

  function scenarioB(px: number) {
    const income = 2;
    const dist = 5;
    const incomeW = (income / maxMonthly) * barAreaW;
    const distW = (dist / maxMonthly) * barAreaW;

    return (
      <g key="B">
        <rect
          x={px}
          y={panelY}
          width={panelW}
          height={panelH}
          rx={8}
          fill={C.dist}
          fillOpacity={0.05}
          stroke={C.dist}
          strokeOpacity={0.35}
        />
        <text x={px + panelW / 2} y={panelY + 18} textAnchor="middle" fontSize="11" fill={C.dist} fontWeight="800">
          {t.caseB}
        </text>
        <text
          x={px + panelW / 2}
          y={panelY + 31}
          textAnchor="middle"
          fontSize="8"
          fill="currentColor"
          fillOpacity={0.55}
        >
          {t.caseBsub}
        </text>

        <text x={px + barAreaX} y={panelY + 52} fontSize="8" fill="currentColor" fillOpacity={0.6} fontWeight="600">
          {t.investmentIncome}
        </text>
        <rect
          x={px + barAreaX}
          y={panelY + 58}
          width={incomeW}
          height={barH}
          rx={2}
          fill={C.nav}
          fillOpacity={0.3}
          stroke={C.nav}
          strokeOpacity={0.6}
        />
        <text x={px + barAreaX + incomeW + 4} y={panelY + 68} fontSize="9" fill={C.nav} fontWeight="700">
          +{fmt(income)}
        </text>

        <text x={px + barAreaX} y={panelY + 90} fontSize="8" fill="currentColor" fillOpacity={0.6} fontWeight="600">
          {t.distributionToInvestors}
        </text>
        <rect
          x={px + barAreaX}
          y={panelY + 96}
          width={distW}
          height={barH}
          rx={2}
          fill={C.dist}
          fillOpacity={0.32}
          stroke={C.dist}
          strokeOpacity={0.55}
        />
        <text
          x={px + barAreaX + distW - 4}
          y={panelY + 106}
          textAnchor="end"
          fontSize="9"
          fill={C.dist}
          fontWeight="700"
        >
          −{fmt(dist)}
        </text>

        <rect
          x={px + barAreaX + incomeW}
          y={panelY + 96}
          width={distW - incomeW}
          height={barH}
          fill={C.dist}
          fillOpacity={0.18}
          strokeDasharray="2 2"
          stroke={C.dist}
          strokeOpacity={0.7}
        />

        {/* Special distribution (return of capital) label */}
        <line
          x1={px + barAreaX + incomeW + (distW - incomeW) / 2}
          y1={panelY + 111}
          x2={px + barAreaX + incomeW + (distW - incomeW) / 2}
          y2={panelY + 118}
          stroke={C.dist}
          strokeWidth="0.8"
          strokeOpacity={0.6}
        />
        <text
          x={px + barAreaX + incomeW + (distW - incomeW) / 2}
          y={panelY + 126}
          textAnchor="middle"
          fontSize="8"
          fill={C.dist}
          fontWeight="700"
        >
          {t.specialDistribution}
        </text>

        <rect
          x={px + 12}
          y={panelY + 134}
          width={panelW - 24}
          height={50}
          rx={6}
          fill={C.dist}
          fillOpacity={0.1}
          stroke={C.dist}
          strokeOpacity={0.5}
        />
        <text x={px + panelW / 2} y={panelY + 148} textAnchor="middle" fontSize="8.5" fill={C.dist} fontWeight="700">
          {t.effectOnNetAssets}
        </text>
        <text x={px + panelW / 2} y={panelY + 166} textAnchor="middle" fontSize="15" fill={C.dist} fontWeight="800">
          −{t.billionYen.replace("{v}", "3")}
        </text>
        <text x={px + panelW / 2} y={panelY + 179} textAnchor="middle" fontSize="8" fill={C.dist} fillOpacity={0.8}>
          {t.shortfallCovered}
        </text>
      </g>
    );
  }

  return (
    <svg viewBox={`0 0 ${VW} ${VH}`} className="mx-auto w-full max-w-xl">
      {scenarioA(startX)}
      {scenarioB(startX + panelW + gap)}
    </svg>
  );
}

export function DocMonthlyBalanceScenariosDiagram() {
  const t = STRINGS[useDocLocale()];
  return (
    <IllustrationCard
      title={t.scenTitle}
      description={t.scenDescription}
    >
      <MonthlyBalanceScenariosIllustration />
    </IllustrationCard>
  );
}
